import {inject} from '@angular/core';
import {ResolveFn, Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';


import {ToastrService} from 'ngx-toastr';

import {EMPTY, forkJoin, Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {UserService} from '../../../shared/user';
import {isUserExpressionField} from '../../../shared/workflow/form/workflow-form.component';
import {FormField, FormModel, Task} from '../../../shared/workflow/workflow-v2.interface';
import {WorkflowV2Service} from '../../../shared/workflow/workflow-v2.service';

const extractTaskDescriptionField = (field: FormField, task: Task) => {
  return of({...field, value: task?.translationKeys?.description, taskVariables: task?.variables});
};

export interface TaskWithFormModel {
  task: Task;
  taskFormModel?: FormModel;
}

export const TaskResolver: ResolveFn<TaskWithFormModel> = (route, state) => {
  const taskId = route.params.taskId;
  const workflowService = inject(WorkflowV2Service);
  const userService = inject(UserService);
  const toastrService = inject(ToastrService);
  const router = inject(Router);
  const translate = inject(TranslateService);

  return workflowService.getTaskById(taskId).pipe(
    switchMap((task: Task) => {
      if (!task || task.status !== 'IN_PROGRESS') {
        toastrService.info(translate.instant('WORKFLOWS.task_is_already_completed'), 'Completed task',
          {
            closeButton: true
          });
        router.navigate(['/inbox/tasks'], {queryParams: {status: 'IN_PROGRESS'}});
        return EMPTY;
      }
      return enrichTaskWithModel(task);
    })
  );

  function enrichTaskWithModel(currentTask: Task): Observable<TaskWithFormModel> {
    if (currentTask?.formKey) {
      return workflowService.getLatestFormModelByFormKey(currentTask.formKey)
        .pipe(
          map((taskFormModel) => ({task: currentTask, taskFormModel})),
          switchMap((taskWithFormModel: TaskWithFormModel) => {
              if (taskWithFormModel.task.id && taskWithFormModel.taskFormModel) {
                const formFields = taskWithFormModel.taskFormModel.fields;
                return extractSpecificFields(formFields, taskWithFormModel.task).pipe(
                  map((specificFields) => ({
                    ...taskWithFormModel,
                    taskFormModel: {
                      ...taskWithFormModel.taskFormModel,
                      fields: mergeFields(specificFields, formFields)
                    }
                  }))
                );
              }
            }
          )
        );
    }
    return of(({task: currentTask}));
  }

  function mergeFields(specificFields: FormField[], formFields: Array<FormField>) {
    const specificFieldsById = specificFields.reduce((acc, formField) => {
      acc[formField.id] = formField;
      return acc;
    }, {});

    return formFields.map(formField => {
      if (!specificFieldsById[formField.id]) {
        return formField;
      } else {
        return specificFieldsById[formField.id];
      }
    });
  }

  function extractSpecificFields(fields: FormField[], currentTask: Task): Observable<FormField[]> {
    const specificFields$ = fields
      .filter(field => field.type === 'expression' || field.type === 'gp-task-description')
      .map(field => {
        if (field.type === 'expression') {
          return extractExpressionField(field, currentTask);
        } else if (field.type === 'gp-task-description') {
          return extractTaskDescriptionField(field, currentTask);
        }
      });

    return forkJoin(specificFields$);
  }

  function extractExpressionField(field: FormField, currentTask: Task): Observable<FormField> {
    const match = field.expression.match(/\$\{([\w ]+)\}/);
    if (match && match.length) {
      const index = currentTask.variables.findIndex(taskVariable => taskVariable.name === match[1]);
      if (index !== -1) {
        const value = currentTask.variables[index].value;
        if (isUserExpressionField(field)) {
          return of(field).pipe(switchMap(() =>
            userService.getUserById(parseInt(value, 0))
              .pipe(map((user) => ({...field, value: user.fullName}))))
          );
        } else {
          return of({...field, value});
        }
      }
    }
    return of({...field, value: field.expression});
  }
};

